import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isBlank } from '@ember/utils';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format } from 'date-fns';
import type { DetailedChangeset } from 'ember-changeset/types';
import { dropTask } from 'ember-concurrency';
import set from 'ember-set-helper/helpers/set';
import { and, not } from 'ember-truth-helpers';
import DatePickerDropdown from 'garaje/components/date-picker-dropdown';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiButton from 'garaje/components/ui-button';
import UiInputField from 'garaje/components/ui-input-field';
import doubleConfirmation from 'garaje/helpers/double-confirmation';
import type Location from 'garaje/models/location';
import type WorkplaceUtilizationDetail from 'garaje/models/workplace-utilization-detail';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type DateLimits from 'garaje/types/date-time/date-limits';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { reads } from 'macro-decorators';

interface LocationWorkplaceUtilizationSignature {
  Args: {
    workplaceUtilizationDetailChangeset: DetailedChangeset<WorkplaceUtilizationDetail>;
    currentLocation: Location;
    onDisable: () => void;
  };
}

export default class LocationWorkplaceUtilization extends Component<LocationWorkplaceUtilizationSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;

  @tracked isSettingsPanelOpen = false;

  @reads('args.workplaceUtilizationDetailChangeset.isNew') isNew!: boolean;
  @reads('args.workplaceUtilizationDetailChangeset.isDirty') isDirty!: boolean;

  get showConfirmation(): boolean {
    if (this.isNew) return false;
    return this.isDirty;
  }

  get saveDisabled(): boolean {
    if (this.isNew) {
      return !this.hasDataChangeset;
    }
    return !this.isDirty;
  }

  get dateLimits(): DateLimits {
    const today = new Date();
    return {
      startDate: today.getTime() / 1000,
    };
  }

  get hasData(): boolean {
    const model = this.args.workplaceUtilizationDetailChangeset.data;
    return Boolean(model.areaSqFt || model.monthlyRent || model.leaseExpirationDate || !isBlank(model.notes?.trim()));
  }

  get hasDataChangeset(): boolean {
    const changeset = this.args.workplaceUtilizationDetailChangeset;
    return Boolean(
      changeset.areaSqFt || changeset.monthlyRent || changeset.leaseExpirationDate || !isBlank(changeset.notes?.trim()),
    );
  }

  get panelDescription(): string {
    const descriptionPieces: string[] = [];

    if (this.hasData) {
      const unchangedModel = this.args.workplaceUtilizationDetailChangeset.data;

      if (unchangedModel.areaSqFt) {
        descriptionPieces.push(`${unchangedModel.areaSqFt} sq ft`);
      }

      if (unchangedModel.monthlyRent) {
        descriptionPieces.push(`$${unchangedModel.monthlyRent}/month`);
      }

      if (unchangedModel.leaseExpirationDate) {
        descriptionPieces.push(`Lease ends ${format(unchangedModel.leaseExpirationDate, 'MM/dd/yyyy')}`);
      }

      const joinedDescription = descriptionPieces.join('  •  ');
      if (joinedDescription.trim()) return joinedDescription;
    }

    return 'Add your workspace size, lease cost, and end date to unlock insights on space utilization and cost efficiency.';
  }

  submitTask = dropTask(async () => {
    try {
      if (!this.hasDataChangeset) {
        await this.args.workplaceUtilizationDetailChangeset.data.destroyRecord();
        this.args.onDisable();
      } else {
        await this.args.workplaceUtilizationDetailChangeset.save();
        this.flashMessages.showAndHideFlash('success', 'Saved!');
      }

      this.isSettingsPanelOpen = false;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  @action
  open(): void {
    this.isSettingsPanelOpen = true;
  }

  @action
  close(): void {
    this.isSettingsPanelOpen = false;
  }

  @action
  rollback(): void {
    if (<boolean>(<unknown>this.args.workplaceUtilizationDetailChangeset.isNew)) return;
    this.args.workplaceUtilizationDetailChangeset.rollback();
  }

  <template>
    <SettingsPanel data-test-workplace-utilization-panel @isOpen={{this.isSettingsPanelOpen}} as |panel|>
      <panel.header
        @iconUri="/assets/images/rooms/room-resizer.svg"
        @title="Workplace details"
        @description={{this.panelDescription}}
        @onCancel={{doubleConfirmation close=this.close rollback=this.rollback dirty=this.showConfirmation}}
        as |SPHeader|
      >
        <SPHeader.controls>
          {{#if (and (not @workplaceUtilizationDetailChangeset.isNew) (not this.isSettingsPanelOpen))}}
            <UiButton {{on "click" this.open}} class="btn btn--small btn--brand" data-test-edit-button>
              Edit
            </UiButton>
          {{else if (and @workplaceUtilizationDetailChangeset.isNew (not this.isSettingsPanelOpen))}}
            <UiButton {{on "click" this.open}} class="btn btn--small btn--brand" data-test-enable-button>
              Enable
            </UiButton>
          {{else}}
            <UiButton
              {{on "click" this.submitTask.perform}}
              class="btn btn--small btn--brand"
              data-test-save-button
              disabled={{this.saveDisabled}}
            >
              Save
            </UiButton>
          {{/if}}
        </SPHeader.controls>
      </panel.header>
      <panel.body as |SPContent|>
        <SPContent class="mb-4">
          <form {{on "submit" this.submitTask.perform}} class="mt-4">
            <div class="flex items-center">
              <UiInputField
                class="w-50"
                @label="Workplace size"
                @placeholder="e.g. 50,000 sq ft"
                @type="number"
                @min="0"
                @value={{@workplaceUtilizationDetailChangeset.areaSqFt}}
                @update={{set @workplaceUtilizationDetailChangeset "areaSqFt"}}
                data-test-workplace-utilization-area-input
              />
              <span class="ml-4">sq ft</span>
            </div>

            <div class="flex items-center">
              <UiInputField
                class="w-50"
                @label="Lease cost per month"
                @placeholder="Enter your $ amount"
                @type="number"
                @min="0"
                @value={{@workplaceUtilizationDetailChangeset.monthlyRent}}
                @update={{set @workplaceUtilizationDetailChangeset "monthlyRent"}}
                data-test-workplace-utilization-monthly-rent-input
              />
              <span class="ml-4">/month</span>
            </div>

            <div class="flex items-center">

              <UiInputField
                class="flex flex-col w-50 mb-5"
                data-test-workplace-utilization-lease-end-date-input
                as |Field|
              >
                <Field.label>
                  Lease end date
                </Field.label>
                <DatePickerDropdown
                  @dateFormat="MM/dd/yyyy"
                  @emptyDatePlaceholder="MM/DD/YYYY"
                  @setEndDate={{set @workplaceUtilizationDetailChangeset "leaseExpirationDate"}}
                  @setStartDate={{set @workplaceUtilizationDetailChangeset "leaseExpirationDate"}}
                  @onDateRangeSelected={{set @workplaceUtilizationDetailChangeset "leaseExpirationDate"}}
                  @onDateSelected={{set @workplaceUtilizationDetailChangeset "leaseExpirationDate"}}
                  @startDate={{@workplaceUtilizationDetailChangeset.leaseExpirationDate}}
                  @endDate={{@workplaceUtilizationDetailChangeset.leaseExpirationDate}}
                  @dateLimits={{this.dateLimits}}
                />
              </UiInputField>

              {{#if @workplaceUtilizationDetailChangeset.leaseExpirationDate}}
                <UiButton
                  class="btn--link !text-gem-50 text-sm ml-4"
                  {{on "click" (set @workplaceUtilizationDetailChangeset "leaseExpirationDate" null)}}
                  data-test-workplace-utilization-clear-lease-end-date-button
                >
                  Clear
                </UiButton>
              {{/if}}
            </div>

            <div class="flex items-center">
              <UiInputField
                class="w-142"
                @placeholder="Add a note about this workplace"
                @value={{@workplaceUtilizationDetailChangeset.notes}}
                @update={{set @workplaceUtilizationDetailChangeset "notes"}}
                data-test-workplace-workplace-utilization-notes-input
                as |Field|
              >
                <Field.label>
                  Notes
                </Field.label>
                <Field.textarea />
              </UiInputField>
            </div>
          </form>
        </SPContent>
      </panel.body>
    </SettingsPanel>
  </template>
}
